import React from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm6_u5_x2_passive_fuehler';
const IMAGE_BASE = '/assets/drawable/';

type TextBlock = { heading: string; body: string };

const textBlocks: TextBlock[] = [
  {
    heading: '',
    body: 'Passive Fühler sind Temperatursensoren, die keine eigene Spannungsversorgung benötigen. Ihr elektrischer Widerstand ändert sich direkt mit der Temperatur. Die DDC misst diesen Widerstand und berechnet daraus die Temperatur. Die gängigsten Typen in der HLK sind PT1000 und verschiedene NTC-Fühler.'
  },
  {
    heading: 'PT1000 Fühler',
    body: `PT1000-Fühler sind Platin-Messwiderstände mit einem Nennwiderstand von **1000 Ohm (Ω) bei 0°C**. Ihr Widerstand steigt nahezu linear mit der Temperatur (ca. +3,85 Ω pro °C). Sie sind bekannt für ihre hohe Genauigkeit und Stabilität.

**Typische Widerstandswerte:**
•   -20°C ≈ 923 Ω
•   **0°C = 1000 Ω**
•   25°C ≈ 1097 Ω
•   50°C ≈ 1194 Ω
•   100°C ≈ 1385 Ω`
  },
  {
    heading: 'NTC Fühler (z.B. NTC10k)',
    body: `NTC-Fühler (Negative Temperature Coefficient) sind Halbleiterwiderstände. Ihr Widerstand **sinkt mit steigender Temperatur** und dies auf eine stark **nichtlineare** Weise. Ein NTC10k hat typischerweise 10 Kiloohm (kΩ) bei 25°C.

**Beispielwerte für NTC10k (B≈3977K):**
•   0°C ≈ 32,65 kΩ
•   **25°C = 10,00 kΩ**
•   50°C ≈ 3,60 kΩ

Sie sind kostengünstig, aber ihre Genauigkeit und Austauschbarkeit sind geringer als bei PT1000-Fühlern.`
  },
  {
    heading: 'Elektrischer Anschluss und Konfiguration',
    body: `Passive Fühler werden üblicherweise in **Zweileitertechnik** an spezielle Analogeingänge (AI) der DDC angeschlossen. Die DDC misst den Widerstand des Fühlers. Der Leitungswiderstand der Anschlusskabel kann bei langen Wegen zu Messungenauigkeiten führen, besonders bei PT1000 (bei NTCs meist weniger kritisch).

**In der DDC-Software muss der exakte Fühlertyp (z.B. 'PT1000' oder 'NTC10k mit B-Wert XXXX') für den jeweiligen Eingang ausgewählt werden.** Nur so kann die DDC den gemessenen Widerstand korrekt in eine Temperatur umrechnen. Eine falsche Konfiguration führt zu völlig falschen Temperaturwerten und Fehlfunktionen der Anlage.`
  },
  {
    heading: '',
    body: 'Passive Temperaturfühler sind einfache und oft kostengünstige Sensoren. Die korrekte Auswahl des Fühlertyps und dessen exakte Konfiguration in der DDC sind entscheidend für genaue Messwerte und eine funktionierende HLK-Regelung.'
  }
];

const images = [
  { id: 'img_fuehler_pt1000', label: 'PT1000 Kanalfühler', src: `${IMAGE_BASE}zzz_help.png`, alt: 'Beispiel eines PT1000 Kanalfühlers' },
  { id: 'img_fuehler_ntc', label: 'NTC10k Raumfühler', src: `${IMAGE_BASE}zzz_help.png`, alt: 'Beispiel eines NTC10k Raumfühlers' },
  { id: 'img_pt1000_skala', label: 'PT1000 Widerstand-Temperatur-Skala', src: `${IMAGE_BASE}zzz_help.png`, alt: 'Skala: PT1000 Widerstand (Ohm) vs. Temperatur (°C)' },
  { id: 'img_ntc_kennlinie', label: 'NTC Kennlinie', src: `${IMAGE_BASE}zzz_help.png`, alt: 'Nichtlineare Kennlinie eines NTC-Fühlers' },
  { id: 'img_schaltplan_anschluss', label: 'Schaltplan passiver Fühler', src: `${IMAGE_BASE}zzz_help.png`, alt: 'Schaltplan: Anschluss passiver Fühler an DDC' }
];

const Page: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const mergedBlocks = React.useMemo(
    () => textBlocks.map((tb, i) => ({ ...tb, body: override?.texts?.[`block_${i}`] || tb.body })),
    [override]
  );

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const fullPlainText = React.useMemo(() => {
    return mergedBlocks.map(b => `${b.heading ? b.heading + '. ' : ''}${b.body}`).join(' ');
  }, [mergedBlocks]);

  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild erfolgreich aktualisiert.');
      await refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const ttsButton = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    'aria-label': 'Text vorlesen',
    sx: { ml: 1 }
  }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(
    UnitPageShell,
    { title: '6.5.2 Passive Fühler (PT1000 / NTC)', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      // Bilder-Spalte
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img =>
          React.createElement(ZoomableImage, {
            key: img.id,
            src: imgSrc(img.id, img.src),
            alt: img.alt,
            label: img.label,
            onReplace: canUploadImages ? (file: File) => handleImageReplace(img.id, file) : undefined
          })
        )
      ),

      // Text-Spalte
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        mergedBlocks.map((block, i) =>
          React.createElement(EditableTextBlock, {
            key: i,
            pageId: PAGE_ID,
            blockId: `block_${i}`,
            heading: block.heading,
            body: block.body,
            onSave: (newBody: string) => handleTextSave(`block_${i}`, newBody)
          })
        ),

        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
          React.createElement(Button, {
            variant: 'outlined',
            onClick: () => navigate('/units/m6_u5_x99_quiz')
          }, 'Zum Quiz')
        )
      )
    )
  );
};

export default Page;


